// Relative-time label for a Supervisor catch, matching the shell's terse
// "when" style (e.g. "2m", "1h", "3d"). Pulled out as a pure function so
// the Activity view stays declarative and the formatting is unit-tested
// without rendering.
//
// `created_at` is the server receive time in MICROSECONDS since the unix
// epoch (the `supervisor_event.created_at` convention — micros, matching the
// backend). When it's missing/zero we fall back to the runner-supplied
// `timestamp` (RFC3339 or empty). `now` is injectable for deterministic
// tests; it defaults to wall-clock millis.

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Format a catch's time as a short relative label. Returns "" when no
 * usable time is available (so the caller can omit the slot). */
export function formatSupervisorEventWhen(
  createdAtMicros: number,
  fallbackTimestamp = "",
  now: number = Date.now(),
): string {
  const ms = resolveMs(createdAtMicros, fallbackTimestamp);
  if (ms === null) return "";

  const diff = now - ms;
  // Future or sub-minute reads as "now" — avoids a jittery "0m" / "-1m".
  if (diff < MINUTE_MS) return "now";
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`;
  return `${Math.floor(diff / DAY_MS)}d`;
}

/** Resolve a millisecond epoch from the micros field, falling back to
 * parsing the RFC3339 runner timestamp. null when neither is usable. */
function resolveMs(createdAtMicros: number, fallbackTimestamp: string): number | null {
  if (Number.isFinite(createdAtMicros) && createdAtMicros > 0) {
    return Math.floor(createdAtMicros / 1000);
  }
  const trimmed = fallbackTimestamp.trim();
  if (trimmed) {
    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
